import type { Driver } from '#domain/Driver.ts';
import type { Vehicle } from '#domain/Vehicle.ts';

import type { ApiDriver, ApiVehicle } from '../model/types.ts';

const isStringArray = (value: unknown): value is string[] =>
    Array.isArray(value) && value.every(item => typeof item === 'string');

const getFleetGroupIds = (vehicle: ApiVehicle): string[] | undefined => {
    const candidate = vehicle as ApiVehicle & {
        group_ids?: unknown;
        groupIds?: unknown;
        fleet_group_ids?: unknown;
        fleetGroupIds?: unknown;
        tags?: unknown;
        groups?: unknown;
    };

    const directIds = candidate.group_ids ?? candidate.groupIds ?? candidate.fleet_group_ids ?? candidate.fleetGroupIds;

    if (isStringArray(directIds)) {
        return directIds;
    }

    const taggedItems = Array.isArray(candidate.tags)
        ? candidate.tags
        : Array.isArray(candidate.groups)
          ? candidate.groups
          : undefined;

    if (!taggedItems) {
        return undefined;
    }

    const ids = taggedItems
        .map(item => (typeof item === 'object' && item !== null && 'id' in item ? item.id : undefined))
        .filter((item): item is string => typeof item === 'string');

    return ids.length > 0 ? ids : undefined;
};

const getFleetGroupName = (vehicle: ApiVehicle): string | undefined => {
    const candidate = vehicle as ApiVehicle & {
        group_name?: unknown;
        groupName?: unknown;
        fleet_group_name?: unknown;
        fleetGroupName?: unknown;
        tags?: unknown;
        groups?: unknown;
    };

    const directName =
        candidate.group_name ?? candidate.groupName ?? candidate.fleet_group_name ?? candidate.fleetGroupName;

    if (typeof directName === 'string' && directName.trim() !== '') {
        return directName;
    }

    const taggedItems = Array.isArray(candidate.tags)
        ? candidate.tags
        : Array.isArray(candidate.groups)
          ? candidate.groups
          : undefined;

    if (!taggedItems) {
        return undefined;
    }

    const names = taggedItems
        .map(item => (typeof item === 'object' && item !== null && 'name' in item ? item.name : undefined))
        .filter((item): item is string => typeof item === 'string' && item.trim() !== '');

    return names.length > 0 ? names.join(', ') : undefined;
};

export const mapDriver = (driver: ApiDriver): Driver => ({
    id: driver.id,
    name: driver.name,
});

export const mapDrivers = (drivers: ApiDriver[]): Driver[] => drivers.map(mapDriver);

export const mapVehicle = (vehicle: ApiVehicle): Vehicle => ({
    id: vehicle.id,
    searchName: vehicle.search_name,
    plate: vehicle.plate,
    fleetGroupIds: getFleetGroupIds(vehicle),
    fleetGroupName: getFleetGroupName(vehicle),
    hasOrderCommunicationLicense: vehicle.has_license,
    hasPocketDriverDataSource: vehicle.has_pocket_driver_data_source ?? undefined,
});

export const mapVehicles = (vehicles: ApiVehicle[]): Vehicle[] => vehicles.map(mapVehicle);
